import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product, Category, ManufacturedProduct, SupplyProductAllocation } from '../../types';
import { useInventory } from '../../context/InventoryContext';
import { useAuth } from '../../context/AuthContext';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Button from '../ui/Button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../ui/Card';
import { supabase } from '../../lib/supabase';
import { AlertTriangle, Check, Info, Loader, Trash, Plus, X } from 'lucide-react';
import ProductAllocationSelect from './ProductAllocationSelect';

interface ProductFormProps {
  product?: Product;
  isEdit?: boolean;
}

interface PriceUpdate {
  id: string;
  new_price: number;
  fetched_at: string;
  status: 'pending' | 'applied' | 'rejected';
}

const ProductForm: React.FC<ProductFormProps> = ({ 
  product,
  isEdit = false
}) => {
  const navigate = useNavigate();
  const { categories, locations, addProduct, updateProduct, deleteProduct } = useInventory();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    price: 0,
    cost_price: 0,
    stock_quantity: 0,
    min_stock_level: 0,
    image: '',
    sku: '',
    location: '',
    purchase_url: '',
    expiration_date: '',
    lot_number: '',
    unit_of_measure: 'each',
  });
  
  const [additionalLocations, setAdditionalLocations] = useState<string[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [manufacturedProducts, setManufacturedProducts] = useState<ManufacturedProduct[]>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingPrice, setIsFetchingPrice] = useState(false);
  const [priceUpdate, setPriceUpdate] = useState<PriceUpdate | null>(null);
  const [showPriceWarning, setShowPriceWarning] = useState(false);
  const [fetchTimeout, setFetchTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    fetchManufacturedProducts();
  }, [user]);

  useEffect(() => {
    if (product && isEdit) {
      setFormData({
        name: product.name,
        description: product.description || '',
        category_id: product.category_id || '',
        price: product.price,
        cost_price: product.cost_price,
        stock_quantity: product.stock_quantity,
        min_stock_level: product.min_stock_level,
        image: product.image || '',
        sku: product.sku,
        location: product.location || '',
        purchase_url: product.purchase_url || '',
        expiration_date: product.expiration_date || '',
        lot_number: product.lot_number || '',
        unit_of_measure: product.unit_of_measure || 'each',
      });
      fetchExistingAllocations();
    } else if (!isEdit) {
      // Reset form to empty values when creating a new supply
      setFormData({
        name: '',
        description: '',
        category_id: '',
        price: 0,
        cost_price: 0,
        stock_quantity: 0,
        min_stock_level: 0,
        image: '',
        sku: '',
        location: '',
        purchase_url: '',
        expiration_date: '',
        lot_number: '',
        unit_of_measure: 'each',
      });
      setAdditionalLocations([]);
      setSelectedProductIds([]);
    }
  }, [product, isEdit]);

  const fetchManufacturedProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('manufactured_products')
        .select('*')
        .order('name');

      if (error) throw error;
      setManufacturedProducts(data || []);
    } catch (error) {
      console.error('Error fetching manufactured products:', error);
    }
  };

  const fetchExistingAllocations = async () => {
    if (!product) return;

    try {
      const { data, error } = await supabase
        .from('supply_product_allocations')
        .select('manufactured_product_id')
        .eq('supply_id', product.id)
        .eq('is_active', true);

      if (error) throw error;
      setSelectedProductIds(data?.map(a => a.manufactured_product_id) || []);
    } catch (error) {
      console.error('Error fetching allocations:', error);
    }
  };

  const addLocationField = () => {
    setAdditionalLocations([...additionalLocations, '']);
  };

  const removeLocationField = (index: number) => {
    const updatedLocations = [...additionalLocations];
    updatedLocations.splice(index, 1);
    setAdditionalLocations(updatedLocations);
  };

  const handleAdditionalLocationChange = (index: number, value: string) => {
    const updatedLocations = [...additionalLocations];
    updatedLocations[index] = value;
    setAdditionalLocations(updatedLocations);
  };

  const checkSkuUniqueness = async (sku: string): Promise<boolean> => {
    if (!user || !sku.trim()) return true;

    try {
      let query = supabase
        .from('supplies')
        .select('id')
        .eq('user_id', user.id)
        .eq('sku', sku.trim());

      // If editing, exclude the current supply from the check
      if (isEdit && product) {
        query = query.neq('id', product.id);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error checking SKU uniqueness:', error);
        return true; // Allow submission if check fails
      }

      return data.length === 0;
    } catch (error) {
      console.error('Error checking SKU uniqueness:', error);
      return true; // Allow submission if check fails
    }
  };

  const validateForm = async () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.sku.trim()) {
      newErrors.sku = 'SKU is required';
    } else {
      // Check SKU uniqueness
      const isSkuUnique = await checkSkuUniqueness(formData.sku);
      if (!isSkuUnique) {
        newErrors.sku = 'This SKU already exists. Please use a unique SKU.';
      }
    }
    
    if (formData.price < 0) {
      newErrors.price = 'Price cannot be negative';
    }
    
    if (formData.cost_price < 0) {
      newErrors.cost_price = 'Cost price cannot be negative';
    }
    
    if (formData.stock_quantity < 0) {
      newErrors.stock_quantity = 'Stock quantity cannot be negative';
    }
    
    if (formData.min_stock_level < 0) {
      newErrors.min_stock_level = 'Minimum stock level cannot be negative';
    }

    if (formData.purchase_url && !isValidUrl(formData.purchase_url)) {
      newErrors.purchase_url = 'Please enter a valid URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const fetchPrice = async (url: string) => {
    if (!isValidUrl(url)) return;
    
    setIsFetchingPrice(true);
    setPriceUpdate(null);
    setShowPriceWarning(false);
    
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-price`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url,
          productId: product?.id
        })
      });

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      // Only fetch price updates if we have a valid priceUpdateId
      if (data.priceUpdateId) {
        const { data: priceUpdate } = await supabase
          .from('price_updates')
          .select('*')
          .eq('id', data.priceUpdateId)
          .single();

        if (priceUpdate) {
          setPriceUpdate(priceUpdate);
          // Automatically update the price field
          setFormData(prev => ({
            ...prev,
            price: priceUpdate.new_price
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching price:', error);
      setErrors(prev => ({
        ...prev,
        purchase_url: error instanceof Error ? error.message : 'Failed to fetch price'
      }));
    } finally {
      setIsFetchingPrice(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Handle automatic price fetching for purchase URL
    if (name === 'purchase_url') {
      // Clear any existing timeout
      if (fetchTimeout) {
        clearTimeout(fetchTimeout);
      }

      if (isValidUrl(value)) {
        setShowPriceWarning(true);
        // Set a timeout to fetch price after user stops typing
        const timeout = setTimeout(() => {
          fetchPrice(value);
        }, 1500); // Wait 1.5 seconds after user stops typing
        setFetchTimeout(timeout);
      } else {
        setShowPriceWarning(false);
        setPriceUpdate(null);
      }
    }
  };

  const handlePriceUpdateAction = async (action: 'apply' | 'reject') => {
    if (!priceUpdate) return;

    try {
      if (action === 'reject') {
        // Revert to original price
        setFormData(prev => ({
          ...prev,
          price: product?.price || 0
        }));
      }

      await supabase
        .from('price_updates')
        .update({
          status: action === 'apply' ? 'applied' : 'rejected'
        })
        .eq('id', priceUpdate.id);

      setPriceUpdate(null);
    } catch (error) {
      console.error('Error updating price:', error);
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = parseFloat(value) || 0;
    setFormData(prev => ({ ...prev, [name]: numValue }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name: string) => (value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const updateAllocations = async (supplyId: string) => {
    if (!user) return;

    try {
      // Get all existing allocations (both active and inactive) to avoid
      // violating the unique constraint on (supply_id, manufactured_product_id)
      const { data: allAllocations, error: fetchError } = await supabase
        .from('supply_product_allocations')
        .select('id, manufactured_product_id, is_active')
        .eq('supply_id', supplyId);

      if (fetchError) throw fetchError;

      const activeAllocations = allAllocations?.filter(a => a.is_active) || [];
      const activeProductIds = activeAllocations.map(a => a.manufactured_product_id);

      // Products to add: selected but not currently active
      const toAdd = selectedProductIds.filter(id => !activeProductIds.includes(id));

      // Products to reactivate: selected, have a deactivated allocation row already
      const toReactivate = allAllocations?.filter(
        a => !a.is_active && selectedProductIds.includes(a.manufactured_product_id)
      ) || [];

      // Deactivate: currently active but no longer selected
      const toRemove = activeAllocations.filter(
        a => !selectedProductIds.includes(a.manufactured_product_id)
      );

      // Fetch BOM data for products being added or reactivated to get quantities
      const productsNeedingQuantity = [...new Set([
        ...toAdd,
        ...toReactivate.map(a => a.manufactured_product_id),
      ])];

      let bomMap: Record<string, number | null> = {};
      if (productsNeedingQuantity.length > 0) {
        const { data: products, error: bomError } = await supabase
          .from('manufactured_products')
          .select('id, bill_of_materials')
          .in('id', productsNeedingQuantity);

        if (bomError) throw bomError;

        for (const product of products || []) {
          let allocatedQuantity = 1;
          if (product.bill_of_materials) {
            const bomItems = Array.isArray(product.bill_of_materials)
              ? product.bill_of_materials
              : [];
            const bomItem = bomItems.find((item: any) => item.supply_id === supplyId);
            if (bomItem?.quantity > 0) {
              allocatedQuantity = bomItem.quantity;
            }
          }
          bomMap[product.id] = allocatedQuantity;
        }
      }

      // Insert brand-new allocations (no existing row at all)
      const toInsert = toAdd.filter(
        productId => !allAllocations?.some(a => a.manufactured_product_id === productId)
      );

      if (toInsert.length > 0) {
        const allocationsToInsert = toInsert.map(productId => ({
          supply_id: supplyId,
          manufactured_product_id: productId,
          allocated_quantity: bomMap[productId] ?? 1,
          is_active: true
        }));

        const { error: insertError } = await supabase
          .from('supply_product_allocations')
          .insert(allocationsToInsert);

        if (insertError) throw insertError;
      }

      // Reactivate existing deactivated allocations
      if (toReactivate.length > 0) {
        for (const allocation of toReactivate) {
          const { error: reactivateError } = await supabase
            .from('supply_product_allocations')
            .update({
              is_active: true,
              allocated_quantity: bomMap[allocation.manufactured_product_id] ?? 1,
            })
            .eq('id', allocation.id);

          if (reactivateError) throw reactivateError;
        }
      }

      // Deactivate removed allocations
      if (toRemove.length > 0) {
        const { error: updateError } = await supabase
          .from('supply_product_allocations')
          .update({ is_active: false })
          .in('id', toRemove.map(a => a.id));

        if (updateError) throw updateError;
      }
    } catch (error) {
      console.error('Error updating allocations:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isValid = await validateForm();
    if (!isValid) {
      console.log('Form validation failed:', errors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      if (isEdit && product) {
        const updatedProduct = {
          ...product,
          name: formData.name,
          description: formData.description,
          category_id: formData.category_id || null,
          price: formData.price,
          cost_price: formData.cost_price,
          stock_quantity: formData.stock_quantity,
          min_stock_level: formData.min_stock_level,
          image: formData.image,
          sku: formData.sku,
          location: formData.location,
          purchase_url: formData.purchase_url,
          lot_number: formData.lot_number || null,
          expiration_date: formData.expiration_date || null,
          unit_of_measure: formData.unit_of_measure
        };

        console.log('Updating product:', updatedProduct);
        await updateProduct(updatedProduct);

        // Update allocations for existing product
        await updateAllocations(product.id);
      } else {
        const newProduct = {
          ...formData,
          expiration_date: formData.expiration_date || undefined,
          lot_number: formData.lot_number || undefined,
        };
        console.log('Adding new product:', newProduct);
        const createdProduct = await addProduct(newProduct);

        // Create allocations for new product
        if (createdProduct?.id) {
          await updateAllocations(createdProduct.id);
        }
      }

      // If we have additional locations, create product_locations records
      if (additionalLocations.length > 0 && additionalLocations.some(loc => loc.trim())) {
        // Get default location for this user
        const { data: defaultLocation } = await supabase
          .from('locations')
          .select('id')
          .eq('user_id', user?.id)
          .eq('is_default', true)
          .single();

        if (defaultLocation) {
          // We would add code here to create product_locations records
          // This would require the product ID, which we would have after creation
          // For now, we'll just log that we would create these records
          console.log('Would create product_locations for:', additionalLocations);
        }
      }

      navigate('/supplies');
    } catch (error: any) {
      console.error('Error saving product:', error);

      // Handle specific Supabase error codes
      if (error?.code === '23505' || error?.message?.includes('duplicate key value violates unique constraint')) {
        if (error?.message?.includes('products_user_id_sku_key') || error?.message?.includes('supplies_user_id_sku_key')) {
          setErrors(prev => ({
            ...prev,
            sku: 'This SKU already exists. Please use a unique SKU.'
          }));
        } else if (error?.message?.includes('supply_product_allocations')) {
          setErrors(prev => ({
            ...prev,
            submit: 'This supply is already allocated to that product. Try removing and re-adding the allocation.'
          }));
        } else {
          setErrors(prev => ({
            ...prev,
            submit: 'A supply with these details already exists. Please check your input.'
          }));
        }
      } else if (error?.code === '22007' && error?.message?.includes('date')) {
        setErrors(prev => ({
          ...prev,
          submit: 'Invalid date format. Please check the expiration date field.'
        }));
      } else if (error?.code === '22P02') {
        setErrors(prev => ({
          ...prev,
          submit: 'Invalid data format. Please check all fields.'
        }));
      } else {
        const errorMessage = error?.message || 'Failed to save supply. Please try again.';
        setErrors(prev => ({
          ...prev,
          submit: errorMessage
        }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProduct = () => {
    if (!product) return;

    if (window.confirm('Are you sure you want to delete this supply? This action cannot be undone.')) {
      deleteProduct(product.id);
      navigate('/supplies');
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (fetchTimeout) {
        clearTimeout(fetchTimeout);
      }
    };
  }, [fetchTimeout]);

  const categoryOptions = [
    { value: '', label: 'Uncategorized' },
    ...categories.map(category => ({
      value: category.id,
      label: category.name,
    }))
  ];

  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>{isEdit ? 'Edit Supply' : 'Add New Supply'}</CardTitle>
          {isEdit && product && (
            <Button
              type="button"
              variant="danger"
              onClick={handleDeleteProduct}
            >
              <Trash className="h-4 w-4 mr-2" />
              Delete Supply
            </Button>
          )}
        </div>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Supply Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              error={errors.name}
              fullWidth
              required
            />
            
            <Select
              label="Category"
              options={categoryOptions}
              value={formData.category_id}
              onChange={handleSelectChange('category_id')}
              error={errors.category_id}
              fullWidth
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Input
                label="Price ($)"
                name="price"
                type="number"
                min="0"
                step="0.01"
                value={formData.price.toString()}
                onChange={handleNumberChange}
                error={errors.price}
                fullWidth
                required
              />
              {isFetchingPrice && (
                <div className="absolute top-full mt-1 w-full bg-blue-50 border border-blue-200 rounded-md p-2">
                  <div className="flex items-center">
                    <Loader className="h-4 w-4 animate-spin text-blue-600 mr-2" />
                    <p className="text-sm text-blue-900">Fetching latest price...</p>
                  </div>
                </div>
              )}
              {priceUpdate && (
                <div className="absolute top-full mt-1 w-full bg-green-50 border border-green-200 rounded-md p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-900">
                        Price updated to ${priceUpdate.new_price}
                      </p>
                      <p className="text-xs text-green-700">
                        Fetched {new Date(priceUpdate.fetched_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="success"
                        onClick={() => handlePriceUpdateAction('apply')}
                        size="sm"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="danger"
                        onClick={() => handlePriceUpdateAction('reject')}
                        size="sm"
                      >
                        <AlertTriangle className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <Input
              label="Cost Price ($)"
              name="cost_price"
              type="number"
              min="0"
              step="0.01"
              value={formData.cost_price.toString()}
              onChange={handleNumberChange}
              error={errors.cost_price}
              fullWidth
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Current Stock"
              name="stock_quantity"
              type="number"
              min="0"
              value={formData.stock_quantity.toString()}
              onChange={handleNumberChange}
              error={errors.stock_quantity}
              fullWidth
              required
            />

            <Input
              label="Minimum Stock Level"
              name="min_stock_level"
              type="number"
              min="0"
              value={formData.min_stock_level.toString()}
              onChange={handleNumberChange}
              error={errors.min_stock_level}
              fullWidth
            />

            <Select
              label="Unit of Measure"
              options={[
                { value: 'each', label: 'Each' },
                { value: 'pair', label: 'Pair' },
                { value: 'box', label: 'Box' },
                { value: 'case', label: 'Case' },
                { value: 'pack', label: 'Pack' },
                { value: 'dozen', label: 'Dozen' },
                { value: 'lbs', label: 'Pounds (lbs)' },
                { value: 'kg', label: 'Kilograms (kg)' },
                { value: 'grams', label: 'Grams' },
                { value: 'oz', label: 'Ounces (oz)' },
                { value: 'tons', label: 'Tons' },
                { value: 'feet', label: 'Feet' },
                { value: 'meters', label: 'Meters' },
                { value: 'yards', label: 'Yards' },
                { value: 'inches', label: 'Inches' },
                { value: 'cm', label: 'Centimeters (cm)' },
                { value: 'gallons', label: 'Gallons' },
                { value: 'liters', label: 'Liters' },
                { value: 'quarts', label: 'Quarts' },
                { value: 'ml', label: 'Milliliters (ml)' },
                { value: 'sqft', label: 'Square Feet' },
                { value: 'sqm', label: 'Square Meters' },
              ]}
              value={formData.unit_of_measure}
              onChange={handleSelectChange('unit_of_measure')}
              fullWidth
              required
            />
          </div>
          
          <Input
            label="SKU"
            name="sku"
            value={formData.sku}
            onChange={handleInputChange}
            error={errors.sku}
            helperText="Stock Keeping Unit - must be unique for your supplies"
            fullWidth
            required
          />
          
          <Select
            label="Location"
            options={[
              { value: '', label: 'Select Location' },
              ...locations.map(location => ({
                value: location.name,
                label: location.name
              }))
            ]}
            value={formData.location}
            onChange={(value) => setFormData(prev => ({ ...prev, location: value }))}
            error={errors.location ? errors.location : ''}
            helperText="Select the primary storage location for this supply"
            fullWidth
          />
          
          <div className="space-y-3">
            {additionalLocations.map((location, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="flex-1">
                  <Select
                    label={`Additional Location ${index + 1}`}
                    options={[
                      { value: '', label: 'Select Location' },
                      ...locations.map(loc => ({
                        value: loc.name,
                        label: loc.name
                      }))
                    ]}
                    value={location}
                    onChange={(value) => handleAdditionalLocationChange(index, value)}
                    fullWidth
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 mt-6"
                  onClick={() => removeLocationField(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          
          <div>
            <Button
              type="button"
              variant="secondary"
              onClick={addLocationField}
              className="mt-2"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Another Location
            </Button>
          </div>

          <div className="border-t pt-4">
            <ProductAllocationSelect
              selectedProductIds={selectedProductIds}
              availableProducts={manufacturedProducts}
              onChange={setSelectedProductIds}
            />
          </div>

          <Input
            label="Image URL"
            name="image"
            value={formData.image}
            onChange={handleInputChange}
            error={errors.image}
            fullWidth
          />

          <div className="relative space-y-2">
            <div className="relative">
              <Input
                label="Purchase URL"
                name="purchase_url"
                type="url"
                value={formData.purchase_url}
                onChange={handleInputChange}
                error={errors.purchase_url}
                helperText="Enter the URL where this supply can be purchased from the supplier. Price will be automatically fetched."
                fullWidth
              />
              {isFetchingPrice && (
                <div className="absolute right-3 top-8">
                  <Loader className="h-4 w-4 animate-spin text-blue-600" />
                </div>
              )}
            </div>

            {showPriceWarning && !isFetchingPrice && !priceUpdate && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800">
                <div className="flex items-start">
                  <Info className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" />
                  <div>
                    <p className="font-medium">Automatic price fetching limitations:</p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>Some websites may block automated access</li>
                      <li>Price information might not be accessible</li>
                      <li>You can always update the price manually if automatic fetching fails</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {formData.purchase_url && !isFetchingPrice && (
              <div className="flex justify-end">
                <a
                  href={formData.purchase_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  View supply page →
                </a>
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Lot Number"
              name="lot_number"
              value={formData.lot_number}
              onChange={handleInputChange}
              helperText="Optional: Batch or lot number for tracking"
              fullWidth
            />

            <Input
              label="Expiration Date"
              name="expiration_date"
              type="date"
              value={formData.expiration_date}
              onChange={handleInputChange}
              helperText="Optional: Expiration or best-by date"
              fullWidth
            />
          </div>

          {errors.submit && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {errors.submit}
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/supplies')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isSubmitting}
          >
            {isEdit ? 'Update Supply' : 'Add Supply'}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default ProductForm;